import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { components } from '@/api/types.gen'

export type AttachmentView = components['schemas']['AttachmentView']
export type AttachmentType = components['schemas']['AttachmentType']

/** What an attachment can belong to. Mirrors the backend's list. */
export type OwnerType =
  | 'part'
  | 'part_lot'
  | 'category'
  | 'footprint'
  | 'storage_location'
  | 'manufacturer'
  | 'supplier'
  | 'project'

export function attachmentsKey(ownerType: OwnerType, ownerId: string) {
  return ['attachments', ownerType, ownerId] as const
}

export async function fetchAttachments(
  ownerType: OwnerType,
  ownerId: string,
): Promise<AttachmentView[]> {
  const { data } = await api.GET('/api/attachments', {
    params: { query: { owner_type: ownerType, owner_id: ownerId } },
  })
  return data ?? []
}

export function useAttachments(ownerType: OwnerType, ownerId: string) {
  return useQuery({
    queryKey: attachmentsKey(ownerType, ownerId),
    queryFn: () => fetchAttachments(ownerType, ownerId),
  })
}

export const attachmentTypesKey = ['attachment-types'] as const

export async function fetchAttachmentTypes(): Promise<AttachmentType[]> {
  const { data } = await api.GET('/api/attachment-types')
  return data ?? []
}

/**
 * Upload goes through `fetch` rather than the generated client: the body is
 * `FormData`, which the typed client models as JSON.
 */
export async function uploadAttachment(input: {
  ownerType: OwnerType
  ownerId: string
  attachmentTypeId: string
  name: string
  file: File
}): Promise<AttachmentView> {
  const form = new FormData()
  form.set('owner_type', input.ownerType)
  form.set('owner_id', input.ownerId)
  form.set('attachment_type_id', input.attachmentTypeId)
  if (input.name.trim()) form.set('name', input.name.trim())
  form.set('file', input.file)

  // No explicit `Origin` header: it is a forbidden header name, so setting it
  // is ignored at best. The browser sends it on every same-origin POST, which is
  // what the backend's CSRF check reads.
  const response = await fetch(`${window.location.origin}/api/attachments`, {
    method: 'POST',
    credentials: 'same-origin',
    body: form,
  })

  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as {
      detail?: string
      errors?: { field: string; message: string }[]
    } | null
    throw new Error(
      problem?.errors?.[0]?.message ?? problem?.detail ?? `The upload failed (${response.status}).`,
    )
  }

  return (await response.json()) as AttachmentView
}

export async function importAttachmentUrl(input: {
  ownerType: OwnerType
  ownerId: string
  attachmentTypeId: string
  url: string
  name?: string
  download: boolean
}) {
  const { data } = await api.POST('/api/attachments/url-import', {
    body: {
      owner_type: input.ownerType,
      owner_id: input.ownerId,
      attachment_type_id: input.attachmentTypeId,
      url: input.url,
      name: input.name,
      download: input.download,
    },
  })
  return data as AttachmentView
}

export async function patchAttachment(
  id: string,
  patch: { name?: string; attachment_type_id?: string; is_preview?: boolean; sort?: number },
) {
  await api.PATCH('/api/attachments/{id}', { params: { path: { id } }, body: patch })
}

export async function deleteAttachment(id: string) {
  await api.DELETE('/api/attachments/{id}', { params: { path: { id } } })
}

/** Where the bytes of an attachment live. */
export function fileUrl(attachment: AttachmentView): string {
  return attachment.external_url ?? `/api/attachments/${attachment.id}/file`
}

export function thumbUrl(id: string): string {
  return `/api/attachments/${id}/thumb`
}

export function isImage(attachment: AttachmentView): boolean {
  return (attachment.mime ?? '').startsWith('image/')
}

export function isPdf(attachment: AttachmentView): boolean {
  return attachment.mime === 'application/pdf'
}

/**
 * Pick the attachment type that fits a file, falling back to the default.
 *
 * Filing a datasheet as "Other" is the kind of small friction that stops people
 * attaching things at all, and the file itself says what it is.
 */
export function guessTypeId(
  file: { name: string; type: string },
  types: { id: string; name: string }[],
  fallback: string,
): string {
  const wanted = file.type.startsWith('image/')
    ? 'Image'
    : file.type === 'application/pdf'
      ? 'Datasheet'
      : /\.(step|stp|stl|wrl)$/i.test(file.name)
        ? '3D model'
        : null
  return types.find((type) => type.name === wanted)?.id ?? fallback
}

/** Human-readable file size. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—'
  const units = ['B', 'kB', 'MB', 'GB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}
